import { useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover'
import { accountLabel } from '@/modules/projects/components/Workbench/accounts'
import { StatementField } from '@/modules/projects/components/Workbench/StatementField'
import type { Account } from '@/modules/projects/components/Workbench/types'
import { cn } from '@/utils'
import { CHOICE, Choice } from './Choice'
import { CombinedNote } from './CombinedNote'

interface AccountFieldProps {
  accounts: Account[]
  accountId: string | null
  onPick: (accountId: string) => void
  // A number the machine never read: the dialog collects it and files this statement under it.
  onAddNew: () => void
}

export const AccountField = ({
  accounts,
  accountId,
  onPick,
  onAddNew,
}: AccountFieldProps) => {
  const [open, setOpen] = useState(false)

  const close = () => setOpen(false)

  const pick = (picked: string) => {
    if (picked !== accountId) onPick(picked)
    close()
  }

  const addNew = () => {
    close()
    onAddNew()
  }

  // lukita files a statement under one number, so a combined statement cannot be moved and a
  // combined account is never somewhere to move one to.
  const filedUnder = accounts.find((account) => account.id === accountId)
  const isCombined = (filedUnder?.numbers.length ?? 0) > 1
  const single = accounts.filter((account) => account.numbers.length === 1)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <StatementField
          field="account"
          missing={!accountId}
          title="File this statement under another account"
        >
          {accountLabel(accounts, accountId)}
        </StatementField>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-60 space-y-0.5 p-1">
        {isCombined && filedUnder ? (
          <CombinedNote account={filedUnder} />
        ) : (
          <>
            <p className="px-2 py-1 text-xs text-ink-subtle">
              File this statement under
            </p>
            {single.map((account) => (
              <Choice
                key={account.id}
                account={account}
                picked={account.id === accountId}
                onPick={pick}
              />
            ))}
            <button
              type="button"
              data-add-account-choice
              onClick={addNew}
              className={cn(CHOICE, 'border-t text-blue-700')}
            >
              New account number…
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
